import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useModalActions } from '@cube3/state/src/redux/Hooks/useModalActions';

import { statuses } from '@cube3/state/src/redux/ducks/request-status';
import { ModalMenuUI } from '@cube3/ui/src/Modal/ModalMenuUI';
import Button from '@cube3/ui/src/Buttons/Button';
import { ExportSingleAssetInfo } from '../prefabs/ExportAssetsInfo';
import { useExportTargetDefaults } from '../../AdminCenter/views/WorkspaceIntegrationsView/hooks/useExportTargetDefaults';
import { useDispatch } from 'react-redux';
import { queueTabs, toggleMediaqueue } from '@cube3/state/src/redux/ducks/ui';
import { hasResolved } from '@cube3/state/src/redux/ducks/request-status/utils/hasResolved';
import { useCurrentWorkspace } from '@cube3/state/src/redux/components/Administration/withCurrentWorkspace';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { MasterDetail } from '@cube3/cubicle/src/core/organisms/MasterDetail';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography';
import RadioExportForm from '../prefabs/RadioExportForm';
import { Scrollbar } from '@cube3/cubicle/src/core/atoms/Scrollbar';
import {
  generateRadioFormId,
  useRadioBroadcastMetadataForms
} from '../hooks/useRadioExportForms';
import ProgressBadgeRenderer from '@cube3/ui/src/exports/subcomponents/ProgressBadgeRenderer';
import { NotificationRecipientsField } from '../prefabs/NotificationRecipientsInput';
import { VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { Divider } from '@cube3/cubicle/src/core/atoms/Divider/Divider';
import { Padding } from '../../Contracts/prefabs/Padding';
import SupportEmailComponent from '@cube3/ui/src/Email/SupportEmailComponent';
import { useBroadcastActions } from '../hooks/useTVBroadcastActions';
import { Asset } from '@cube3/common/model/schema/resources/asset';
import { CreateExportsData } from '@cube3/common/model/types';
import { RadioExportModalProps } from './RadioExportDestinationModal';

interface Props {
  modalContext: RadioExportModalProps; // assets, broadcasters getting passed from the destination modal
}

export const RadioExportModal: React.FC<Props> = (props) => {
  const { assets: selection, stations } = props.modalContext;
  const [workspaceId] = useCurrentWorkspace();

  const { resources: exportTargets, loading: loadingExportTargets } =
    useResourceList__ALPHA({
      resourceId: workspaceId,
      resourceType: 'workspace',
      edgeType: 'export-target',
      edgeLabel: 'export-targets',
      params: {
        filter: {
          type: 'radio'
        }
      }
    });

  const exportTargetId = exportTargets[0]?.id;

  const { previousModal, closeAllModals, goBackModals } = useModalActions();

  const {
    allFormValues,
    allFormsValid,
    destroyAllForms,
    formSubmitting,
    allFormErrors
  } = useRadioBroadcastMetadataForms(selection, exportTargetId);

  const { createBatchStatus, errorMessage, createBatch } = useBroadcastActions<
    CreateExportsData<'radio'>
  >({ assets: selection as Asset[], getForm: generateRadioFormId });

  const [, defaultsStatus] = useExportTargetDefaults('radio');

  const [recipients, setRecipients] = useState([]);
  const handleSubmit = useCallback(() => {
    if (!allFormValues || allFormValues.length === 0) return;

    const exportsWithBroadcasters = allFormValues.map((values) => ({
      ...values,
      parameters: {
        ...values.parameters,

        medialab: {
          ...values.parameters.medialab,
          stations
        }
      }
    }))
    createBatch({
      recipients: recipients,
      exports: exportsWithBroadcasters
    });
  }, [createBatch, allFormValues, recipients, stations]);

  const dispatch = useDispatch();

  // open export status view modal
  useEffect(() => {
    if (createBatchStatus === statuses.SUCCESS) {
      destroyAllForms();
      closeAllModals();
      dispatch(toggleMediaqueue(true, queueTabs.EXPORTS));
    }
  }, [createBatchStatus, closeAllModals, destroyAllForms, dispatch]);

  const handleCancel = useCallback(() => {
    goBackModals(2);
    destroyAllForms();
  }, [goBackModals]);

  const loading =
    loadingExportTargets ||
    formSubmitting ||
    (createBatchStatus && !hasResolved(createBatchStatus)) ||
    (defaultsStatus && !hasResolved(defaultsStatus));

  return (
    <ModalMenuUI
      title="Export to Radio • Options"
      extraWide={true}
      multiplyContentSpace={10}
      noScroll
      loading={loading}
      onCloseEvent={handleCancel}
      footerLeftComponent={
        <Button
          text={'Previous'}
          onClick={previousModal}
          colorVariant={'ghost2'}
        />
      }
      defineMargins="noMargins"
      footerRightComponent={
        <>
          <Button
            text={'Cancel'}
            colorVariant={'ghost2'}
            onClick={handleCancel}
            disabled={false}
          />
          <Button
            disabled={!allFormsValid?.allValid}
            colorVariant={'filled1'}
            text={'Export'}
            onClick={handleSubmit}
          />
        </>
      }
    >
      <VBox justify="start" style={{ width: '100%', height: '100%' }}>
        {createBatchStatus === statuses.FAILED && (
          <Padding>
            {errorMessage ? (
              <Typography color="critical">{errorMessage}</Typography>
            ) : (
              <Typography color="critical">
                Something went wrong with your request, please try again later
                or contact <SupportEmailComponent />
              </Typography>
            )}
          </Padding>
        )}
        {!loading && selection?.length > 1 && (
          <div style={{ marginTop: '16px' }}>
            <Padding padding={'0 0 0 70px'}>
              <NotificationRecipientsField
                recipients={recipients}
                setRecipients={setRecipients}
              />
            </Padding>
            <Divider />
          </div>
        )}
        {selection && selection.length > 1 && (
          <MasterDetail
            listElement={
              <>
                {selection.map((asset) => (
                  <div key={asset.id} data-id={asset.id}>
                    <ExportSingleAssetInfo
                      selection={asset}
                      hideText={true}
                      width={60}
                    />
                    <ProgressBadgeRenderer
                      form={allFormErrors.errors[asset.id]}
                    />
                  </div>
                ))}
              </>
            }
            foldable
            listHeaderElement={
              <Typography>{selection.length} assets</Typography>
            }
            detailElement={
              <>
                {selection.map((asset) => (
                  <Padding key={asset.id} data-id={asset.id}>
                    <RadioExportForm asset={asset} />
                  </Padding>
                ))}
              </>
            }
          />
        )}

        {selection.length === 1 ? (
          <Scrollbar>
            <VBox gap={16} style={{ padding: 16 }}>
              <ExportSingleAssetInfo selection={selection?.[0]} />
              <Divider />

              <RadioExportForm asset={selection[0]} />
              <Divider />
              <div style={{ marginLeft: '-8px' }}>
                <NotificationRecipientsField
                  setRecipients={setRecipients}
                  recipients={recipients}
                />
              </div>
            </VBox>
          </Scrollbar>
        ) : null}
      </VBox>
    </ModalMenuUI>
  );
};
